import {
    Button,
    Chip,
    Fieldset,
    Heading,
    Label,
    Paragraph,
    Search,
    Select,
    EXPERIMENTAL_Suggestion as Suggestion,
} from "@digdir/designsystemet-react";
import { useMemo, useState } from "react";
import { AreaContainer } from "../shared/AreaContainer";
import { FlexContainer } from "../shared/FlexContainer";
import { RapportkatalogCard } from "./components/card";
import { RapportkatalogTable } from "./components/table";
import { getRapportkatalogRows } from "./data/rapportkatalogDataSource";
import { isLastChangedField, type SortOption, useRapportkatalogFilterState } from "./filter";
import { renderDetailValue, renderLastChangedValue } from "./utils";
import "./Rapportkatalog.css";
import { SquareGridIcon, TableIcon } from "@navikt/aksel-icons";

type ViewMode = "cards" | "table";

function Rapportkatalog() {
    const rows = useMemo(getRapportkatalogRows, []);
    const [viewMode, setViewMode] = useState<ViewMode>("cards");

    const {
        searchTerm,
        setSearchTerm,
        sortOption,
        sortDirection,
        setSortOptionWithDefaultDirection,
        setSortFromHeaderClick,
        selectedAreas,
        setSelectedAreas,
        selectedOwners,
        setSelectedOwners,
        selectedTargetGroups,
        setSelectedTargetGroups,
        areaOptions,
        ownerOptions,
        targetGroupOptions,
        visibleRows,
    } = useRapportkatalogFilterState(rows);

    const activeFilterItems = [
        ...selectedAreas.map((item) => ({ key: "Område", value: item.value, label: item.label })),
        ...selectedOwners.map((item) => ({ key: "Eier", value: item.value, label: item.label })),
        ...selectedTargetGroups.map((item) => ({ key: "Målgruppe", value: item.value, label: item.label })),
    ];

    const hasActiveFilters = activeFilterItems.length > 0;

    return (
        <AreaContainer>
            <FlexContainer flexDirection="column" gap="var(--ds-size-8)" width="100%" alignItems="flex-start">
                <section>
                    <Heading level={1} data-size="xl" style={{ marginBottom: "var(--ds-size-2)" }}>
                        Rapportkatalog
                    </Heading>
                    <Paragraph data-size="md">Her kan det stå noe beskrivende om hva som vises på siden</Paragraph>
                </section>

                <section aria-label="Søk, sortering og filtrering">
                    <FlexContainer>
                        <Fieldset className="rapportkatalog-search-fieldset">
                            <Fieldset.Legend>Søk i rapportkatalogen</Fieldset.Legend>
                            <Search className="rapportkatalog-search">
                                <Search.Input
                                    aria-label="Søk i rapportkatalogen"
                                    value={searchTerm}
                                    onChange={(event) => {
                                        setSearchTerm(event.target.value);
                                    }}
                                />
                                <Search.Clear
                                    aria-label="Tøm søk"
                                    onClick={() => {
                                        setSearchTerm("");
                                    }}
                                />
                            </Search>
                        </Fieldset>

                        <Fieldset className="rapportkatalog-filter-fieldset">
                            <Fieldset.Legend>Filtrer etter</Fieldset.Legend>
                            <FlexContainer marginTop={0}>
                                <Suggestion
                                    className="rapportkatalog-filter"
                                    multiple
                                    selected={selectedAreas}
                                    onSelectedChange={setSelectedAreas}
                                >
                                    <Suggestion.Input aria-label="Filtrer på område" placeholder="Område" />
                                    <Suggestion.Clear />
                                    <Suggestion.List>
                                        <Suggestion.Empty>Ingen valg</Suggestion.Empty>
                                        {areaOptions.map((option) => (
                                            <Suggestion.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Suggestion.Option>
                                        ))}
                                    </Suggestion.List>
                                </Suggestion>

                                <Suggestion
                                    className="rapportkatalog-filter"
                                    multiple
                                    selected={selectedOwners}
                                    onSelectedChange={setSelectedOwners}
                                >
                                    <Suggestion.Input aria-label="Filtrer på eier" placeholder="Eier" />
                                    <Suggestion.Clear />
                                    <Suggestion.List>
                                        <Suggestion.Empty>Ingen valg</Suggestion.Empty>
                                        {ownerOptions.map((option) => (
                                            <Suggestion.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Suggestion.Option>
                                        ))}
                                    </Suggestion.List>
                                </Suggestion>

                                <Suggestion
                                    className="rapportkatalog-filter"
                                    multiple
                                    selected={selectedTargetGroups}
                                    onSelectedChange={setSelectedTargetGroups}
                                >
                                    <Suggestion.Input aria-label="Filtrer på målgruppe" placeholder="Målgruppe" />
                                    <Suggestion.Clear />
                                    <Suggestion.List>
                                        <Suggestion.Empty>Ingen valg</Suggestion.Empty>
                                        {targetGroupOptions.map((option) => (
                                            <Suggestion.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Suggestion.Option>
                                        ))}
                                    </Suggestion.List>
                                </Suggestion>
                            </FlexContainer>
                        </Fieldset>

                        <Fieldset className="rapportkatalog-sort-fieldset">
                            <Fieldset.Legend>Sorter etter</Fieldset.Legend>
                            <Label htmlFor="rapportkatalog-sort" className="sr-only">
                                Sorter etter
                            </Label>
                            <Select
                                id="rapportkatalog-sort"
                                value={sortOption}
                                onChange={(event) => {
                                    setSortOptionWithDefaultDirection(event.target.value as SortOption);
                                }}
                                className="rapportkatalog-search"
                            >
                                <Select.Option value="title">Tittel</Select.Option>
                                <Select.Option value="area">Område</Select.Option>
                                <Select.Option value="owner">Eier</Select.Option>
                                <Select.Option value="targetGroups">Målgruppe</Select.Option>
                                <Select.Option value="lastChanged">Sist oppdatert</Select.Option>
                            </Select>
                        </Fieldset>
                    </FlexContainer>

                    {hasActiveFilters && (
                        <fieldset className="rapportkatalog-active-filters-row">
                            <legend className="sr-only">Valgte filtre</legend>
                            {activeFilterItems.map((item) => (
                                <Chip.Removable
                                    key={`${item.key}-${item.value}`}
                                    onClick={() => {
                                        if (item.key === "Område") {
                                            setSelectedAreas((previousItems) =>
                                                previousItems.filter(
                                                    (selectedItem) => selectedItem.value !== item.value,
                                                ),
                                            );
                                            return;
                                        }

                                        if (item.key === "Eier") {
                                            setSelectedOwners((previousItems) =>
                                                previousItems.filter(
                                                    (selectedItem) => selectedItem.value !== item.value,
                                                ),
                                            );
                                            return;
                                        }

                                        setSelectedTargetGroups((previousItems) =>
                                            previousItems.filter((selectedItem) => selectedItem.value !== item.value),
                                        );
                                    }}
                                >
                                    {item.key}: {item.label}
                                </Chip.Removable>
                            ))}

                            <Chip.Button
                                onClick={() => {
                                    setSelectedAreas([]);
                                    setSelectedOwners([]);
                                    setSelectedTargetGroups([]);
                                }}
                            >
                                Nullstill alle filtre
                            </Chip.Button>
                        </fieldset>
                    )}
                </section>

                <section style={{ width: "100%" }}>
                    <FlexContainer alignItems="center" justifyContent="space-between" width="100%">
                        <Paragraph data-size="sm" role="status">
                            {visibleRows.length} rapporter funnet
                        </Paragraph>

                        <div className="rapportkatalog-view-toggle">
                            <Paragraph data-size="sm" style={{ marginRight: "var(--ds-size-2)" }}>
                                Vis som:
                            </Paragraph>

                            <Button
                                aria-label="Vis som kort"
                                title="Vis som kort"
                                icon
                                variant={viewMode === "cards" ? "secondary" : "tertiary"}
                                onClick={() => {
                                    setViewMode(() => "cards");
                                }}
                            >
                                <SquareGridIcon aria-hidden="true" />
                            </Button>
                            <Button
                                aria-label="Vis som tabell"
                                title="Vis som tabell"
                                icon
                                variant={viewMode === "table" ? "secondary" : "tertiary"}
                                onClick={() => {
                                    setViewMode(() => "table");
                                }}
                            >
                                <TableIcon aria-hidden="true" />
                            </Button>
                        </div>
                    </FlexContainer>
                </section>

                <section>
                    {viewMode === "table" ? (
                        <RapportkatalogTable
                            rows={visibleRows}
                            isLastChangedField={isLastChangedField}
                            renderDetailValue={renderDetailValue}
                            renderLastChangedValue={renderLastChangedValue}
                            sortOption={sortOption}
                            sortDirection={sortDirection}
                            onSortHeaderClick={setSortFromHeaderClick}
                        />
                    ) : (
                        <div className="rapportkatalog-card-list">
                            {visibleRows.map((row) => {
                                return (
                                    <RapportkatalogCard
                                        key={row.id}
                                        title={row.title}
                                        area={row.area}
                                        owner={row.owner}
                                        targetGroups={row.targetGroups}
                                        shortDescription={row.shortDescription}
                                        detailFields={row.detailFields}
                                        isLastChangedField={isLastChangedField}
                                        renderDetailValue={renderDetailValue}
                                        renderLastChangedValue={renderLastChangedValue}
                                    />
                                );
                            })}
                        </div>
                    )}
                </section>
            </FlexContainer>
        </AreaContainer>
    );
}

export default Rapportkatalog;
